import { useState } from 'react';
import { RefreshCw, Pencil, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  useRevenueHypothesis,
  useCalibrateHypothesis,
  useUpsertHypothesis,
} from '../../hooks/useRevenueHypothesis.js';
import { formatCurrency } from '../../lib/format.js';
import RevenueHypothesisEditModal from './RevenueHypothesisEditModal.jsx';

// Noms abrégés des mois (initiales FR)
const MONTH_LABELS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

/**
 * Mini-graphique saisonnalité en CSS pur (12 barres).
 * Les barres sont proportionnelles au coef, normalisées sur max 32px.
 */
function SeasonalityBars({ coefs }) {
  if (!coefs?.length) return null;
  const maxCoef = Math.max(...coefs, 0.01);

  return (
    <div className="mt-3">
      <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-2">
        Saisonnalite (×coef)
      </p>
      <div className="flex items-end gap-0.5 h-9">
        {coefs.map((c, i) => {
          const h = Math.max(4, Math.round((c / maxCoef) * 32));
          return (
            <div key={i} className="flex flex-col items-center gap-0.5" style={{ flex: 1 }}>
              <div
                className="w-full rounded-t bg-blue-400"
                style={{ height: `${h}px` }}
                title={`${MONTH_LABELS[i]} : ×${c.toFixed(2)}`}
                aria-label={`${MONTH_LABELS[i]} coefficient ${c.toFixed(2)}`}
              />
              <span className="text-[9px] text-slate-400 leading-none select-none">
                {MONTH_LABELS[i]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Modal de confirmation après auto-calibration.
 * Affiche le récap et propose d'appliquer ou d'annuler.
 */
function CalibrateConfirmModal({ result, onApply, onCancel, isApplying }) {
  if (!result) return null;

  const baseline = formatCurrency(result.baseline_cents_monthly);
  const total = formatCurrency(result.total_year_cents);
  const coefs = result.seasonality_coefs ?? [];
  const maxCoef = Math.max(...coefs, 0.01);

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        aria-hidden
        onClick={onCancel}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Confirmer la calibration"
        className="fixed inset-x-4 bottom-4 z-50 rounded-2xl bg-white shadow-2xl p-5 max-w-sm mx-auto sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:w-full"
      >
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 size={18} className="text-green-500 shrink-0" />
          <h3 className="text-sm font-semibold text-slate-900">Calibration 2025</h3>
        </div>

        <div className="space-y-1 text-sm mb-4">
          <p className="text-slate-700">
            CA {result.year ?? 2025} :{' '}
            <strong className="text-slate-900">{total}</strong>
            {result.source_tx_count != null && (
              <span className="text-slate-400 ml-1 text-xs">
                sur {result.source_tx_count.toLocaleString('fr-FR')} transactions
              </span>
            )}
          </p>
          <p className="text-slate-700">
            Baseline calcul&eacute;e :{' '}
            <strong className="text-slate-900">{baseline}/mois</strong>
          </p>
        </div>

        <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">
          Saisonnalite detectee
        </p>
        <div className="flex items-end gap-0.5 h-8 mb-4">
          {coefs.map((c, i) => {
            const h = Math.max(3, Math.round((c / maxCoef) * 28));
            return (
              <div key={i} className="flex flex-col items-center gap-0" style={{ flex: 1 }}>
                <div
                  className="w-full rounded-t bg-blue-400"
                  style={{ height: `${h}px` }}
                />
                <span className="text-[8px] text-slate-400 leading-none">
                  {MONTH_LABELS[i]}
                </span>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isApplying}
            className="flex-1 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={isApplying}
            className="flex-1 py-2 rounded-lg bg-slate-900 text-sm font-medium text-white hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-50"
          >
            {isApplying ? 'Enregistrement…' : 'Appliquer'}
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * Carte hypothese de chiffre d'affaires.
 * Placee en tete de la section REVENUS dans /previsionnels.
 */
export default function RevenueHypothesisCard() {
  const { data: hypothesis, isLoading } = useRevenueHypothesis();
  const calibrate = useCalibrateHypothesis();
  const upsert = useUpsertHypothesis();

  const [calibrateResult, setCalibrateResult] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  async function handleCalibrate() {
    try {
      const result = await calibrate.mutateAsync({ year: 2025 });
      setCalibrateResult(result);
    } catch {
      // L'erreur est accessible via calibrate.error
    }
  }

  async function handleApplyCalibrateResult() {
    if (!calibrateResult) return;
    await upsert.mutateAsync({
      id: hypothesis?.id,
      baseline_cents_monthly: calibrateResult.baseline_cents_monthly,
      seasonality_coefs: calibrateResult.seasonality_coefs,
      // start_year = année source de la calibration → growth s'applique dès l'année suivante.
      // Sans ce champ, start_year=2026 par défaut → growth × (1+x)^0 = pas d'effet sur 2026.
      start_year: calibrateResult.year ?? 2025,
      // Conserve les autres champs existants si hypothèse déjà en DB
      growth_pct_yearly: hypothesis?.growth_pct_yearly ?? 0,
      pessimiste_variance_pct: hypothesis?.pessimiste_variance_pct ?? -15,
      optimiste_variance_pct: hypothesis?.optimiste_variance_pct ?? 15,
    });
    setCalibrateResult(null);
  }

  if (isLoading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4 animate-pulse">
        <div className="h-4 w-48 rounded bg-slate-200 mb-3" />
        <div className="h-3 w-32 rounded bg-slate-100" />
      </div>
    );
  }

  // Etat vide
  if (!hypothesis) {
    return (
      <>
        <div className="bg-white border border-blue-200 rounded-xl p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2">
            <AlertCircle size={16} className="text-blue-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-slate-800">
                Hypothese de chiffre d&apos;affaires
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Pas encore configuree. Calibrez depuis votre CA 2025 ou saisissez manuellement.
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleCalibrate}
              disabled={calibrate.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-60"
            >
              <RefreshCw size={12} className={calibrate.isPending ? 'animate-spin' : ''} />
              {calibrate.isPending ? 'Calibration…' : 'Auto-calibrer 2025'}
            </button>
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
            >
              <Pencil size={12} />
              Saisie manuelle
            </button>
          </div>
          {calibrate.error && (
            <p className="text-xs text-red-600 w-full">{calibrate.error.message}</p>
          )}
        </div>

        {calibrateResult && (
          <CalibrateConfirmModal
            result={calibrateResult}
            onApply={handleApplyCalibrateResult}
            onCancel={() => setCalibrateResult(null)}
            isApplying={upsert.isPending}
          />
        )}

        <RevenueHypothesisEditModal
          isOpen={editOpen}
          onClose={() => setEditOpen(false)}
          hypothesis={null}
        />
      </>
    );
  }

  // Etat configuré
  const baseline = formatCurrency(hypothesis.baseline_cents_monthly);
  const growth = hypothesis.growth_pct_yearly ?? 0;
  const pessimiste = hypothesis.pessimiste_variance_pct ?? -15;
  const optimiste = hypothesis.optimiste_variance_pct ?? 15;
  const coefs = hypothesis.seasonality_coefs ?? [];

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        {/* Titre */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
            Hypothese de chiffre d&apos;affaires
          </p>
          {hypothesis.name && hypothesis.name !== 'Hypothèse principale' && (
            <span className="text-xs text-slate-500 italic">{hypothesis.name}</span>
          )}
        </div>

        {/* KPIs ligne */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm mb-1">
          <div>
            <span className="text-xs text-slate-500">Baseline mensuelle</span>
            <p className="font-semibold text-slate-900 tabular-nums">{baseline}</p>
          </div>
          <div>
            <span className="text-xs text-slate-500">Croissance</span>
            <p className="font-semibold text-slate-900">
              {growth >= 0 ? '+' : ''}{growth}&nbsp;%/an
            </p>
          </div>
        </div>

        {/* Saisonnalite */}
        {coefs.length === 12 && <SeasonalityBars coefs={coefs} />}

        {/* Variances */}
        <div className="mt-3 flex gap-4 text-xs text-slate-600">
          <span>
            Pessimiste{' '}
            <strong className="text-red-600">
              {pessimiste >= 0 ? '+' : ''}{pessimiste}&nbsp;%
            </strong>
          </span>
          <span>
            Optimiste{' '}
            <strong className="text-green-600">
              {optimiste >= 0 ? '+' : ''}{optimiste}&nbsp;%
            </strong>
          </span>
        </div>

        {/* Actions */}
        <div className="mt-4 flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleCalibrate}
            disabled={calibrate.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-60"
          >
            <RefreshCw size={12} className={calibrate.isPending ? 'animate-spin' : ''} />
            {calibrate.isPending ? 'Calibration…' : 'Auto-calibrer 2025'}
          </button>
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            <Pencil size={12} />
            Modifier
          </button>
        </div>

        {calibrate.error && (
          <p className="mt-2 text-xs text-red-600">{calibrate.error.message}</p>
        )}
      </div>

      {/* Modal confirmation calibration */}
      {calibrateResult && (
        <CalibrateConfirmModal
          result={calibrateResult}
          onApply={handleApplyCalibrateResult}
          onCancel={() => setCalibrateResult(null)}
          isApplying={upsert.isPending}
        />
      )}

      {/* Modal edition */}
      <RevenueHypothesisEditModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        hypothesis={hypothesis}
      />
    </>
  );
}
